import {
  Badge,
  Box,
  Checkbox,
  HStack,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";

type CreativeAdsetOption = {
  id: string;
  name: string;
  disabled?: boolean;
};

type CreativeAdsetTargetingCardProps = {
  adsets: CreativeAdsetOption[];
  selectedAdsetIds: string[];
  addToAllAdsets: boolean;
  isAddToAllAdsetsDisabled?: boolean;
  onToggleAddToAllAdsets: (checked: boolean) => void;
  onToggleAdset: (adsetId: string, checked: boolean) => void;
};

export default function CreativeAdsetTargetingCard({
  adsets,
  selectedAdsetIds,
  addToAllAdsets,
  isAddToAllAdsetsDisabled = false,
  onToggleAddToAllAdsets,
  onToggleAdset,
}: CreativeAdsetTargetingCardProps) {
  const selectedCount = selectedAdsetIds.length;

  return (
    <VStack align="stretch" spacing={4} py={4}>
      <HStack spacing={2}>
        <CheckCircleIcon color="green.500" />
        <Text fontSize="2xl" fontWeight={700} color="gray.800">
          Ad sets
        </Text>
      </HStack>

      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        py={4}
        px={6}
        bg="white"
      >
        <HStack justify="space-between" w="full" mb={2} align="flex-start">
          <VStack align="flex-start" spacing={1} pr={4}>
            <HStack>
              <Text fontSize="md" fontWeight="semibold" color="gray.800">
                Add to all adsets
              </Text>
              <Badge
                color="green.100"
                backgroundColor="green.500"
                rounded="md"
                textTransform="none"
                fontWeight="semibold"
                px="8px"
                py="2px"
              >
                Recommended
              </Badge>
            </HStack>
            <Text fontSize="sm" color="gray.600">
              We use existing KYD data and Meta AI to find the best target
              audiences for this campaign.
            </Text>
          </VStack>
          <Switch
            size="lg"
            isChecked={addToAllAdsets}
            isDisabled={isAddToAllAdsetsDisabled}
            onChange={(e) => onToggleAddToAllAdsets(e.target.checked)}
          />
        </HStack>
        {addToAllAdsets && (
          <Text fontSize="sm" color="gray.600" mt={1}>
            All available ad sets will receive this creative.
          </Text>
        )}
      </Box>

      {!addToAllAdsets && (
        <Box
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="xl"
          p={6}
          bg="white"
        >
          <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={3}>
            Select ad sets ({selectedCount})
          </Text>
          <Stack spacing={3}>
            {adsets.map((adset) => {
              const checked = selectedAdsetIds.includes(adset.id);
              return (
                <HStack
                  key={adset.id}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={3}
                  py={3}
                  justify="space-between"
                  opacity={adset.disabled ? 0.6 : 1}
                >
                  <Text fontSize="md" fontWeight={500} color="gray.800">
                    {adset.name}
                  </Text>
                  <Checkbox
                    isChecked={checked}
                    isDisabled={adset.disabled}
                    onChange={(e) => onToggleAdset(adset.id, e.target.checked)}
                    colorScheme="blue"
                    size="lg"
                  />
                </HStack>
              );
            })}
          </Stack>
        </Box>
      )}
    </VStack>
  );
}
